import { useRef } from "react";
import PropTypes from "prop-types";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/utils/Button";
import { Background } from "@/components/utils/Background";
import { Eyebrow } from "@/components/utils/Eyebrow";
import { usePadding } from "@/components/utils/Padding";
import "./styles.scss";

/* #META
 {
  "NAME": "Selected Post",
  "CLASSNAME": "selected-post-section",
  "URL": "http://nicasource.local"
 }
#ENDMETA */

// Helpers
function getTagStrings(tags) {
  if (!Array.isArray(tags)) return [];
  return tags
    .map((t) => (typeof t?.tag_text === "string" ? t.tag_text.trim() : ""))
    .filter(Boolean);
}

const SelectedPost = ({ data }) => {
  const mode = data?.content_type;
  const isSingle = mode === "single_post" || (!mode && data?.selected_post);
  const paddingComponent = data?.padding_component;
  const eyebrowComponent = data?.content?.eyebrow_component;

  const paddingStyle = usePadding(paddingComponent);

  // single post
  const postInfo = data?.selected_post;
  const postImg = postInfo?.featured_image;
  const postTitle = postInfo?.post_title || "";
  const postCategories = Array.isArray(postInfo?.categories) ? postInfo.categories : [];

  const categoriesRef = useRef(null);
  const categoriesInView = useInView(categoriesRef, { once: true, threshold: 0.5 });

  // flexible content
  const content = data?.content || {};
  const headingHtml = content?.heading_text || "";

  // background component
  const backgroundComponent = content?.background_component || {};

  // CTA / Tags
  const tags = getTagStrings(content?.tags);
  const tagsPosition = content?.tags_position;
  const showCTAFlag = content?.show_button_tags;
  const buttonComponent = content?.button_component;

  const hasTags = tags.length > 0;

  const showCTA = showCTAFlag === "button";
  const showTags = showCTAFlag === "tags" && hasTags;

  const renderContainer = ({ backgroundStyle, useBackgroundComponent = false, children }) => (
    <div className="selected-post-container" style={backgroundStyle}>
      {!isSingle && useBackgroundComponent && <Background asBackground {...backgroundComponent} />}
      <div className="overlay" />
      <div className="inner-container">{children}</div>
    </div>
  );

  return (
    <section className="selected-post-section selected-post container-revamp" style={paddingStyle}>
      {isSingle
        ? renderContainer({
            backgroundStyle: postImg ? { backgroundImage: `url(${postImg})` } : undefined,
            useBackgroundComponent: false,
            children: (
              <>
                {postTitle && (
                  <a href={postInfo?.post_url} className="selected-post-title">
                    {postTitle}
                  </a>
                )}

                {postCategories.length > 0 && (
                  <div className="post-categories" ref={categoriesRef}>
                    {postCategories.map((category, index) => (
                      <motion.span
                        key={`cat-${index}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={categoriesInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: index * 0.2 }}
                        className="category-item"
                      >
                        {category}
                      </motion.span>
                    ))}
                  </div>
                )}
              </>
            ),
          })
        : renderContainer({
            backgroundStyle: undefined,
            useBackgroundComponent: true,
            children: (
              <>
                {eyebrowComponent && (
                  <>
                    {/* Eyebrow */}
                    <Eyebrow {...eyebrowComponent} />
                  </>
                )}

                {headingHtml && (
                  <div
                    className="selected-post-heading"
                    dangerouslySetInnerHTML={{ __html: headingHtml }}
                  />
                )}

                {showTags && (
                  <div className="tags-container" style={{ justifyContent: tagsPosition }}>
                    {tags.map((tag, i) => (
                      <div className="tag" key={`tag-${i}`}>
                        {tag}
                      </div>
                    ))}
                  </div>
                )}

                {showCTA && buttonComponent && <Button {...buttonComponent} />}
              </>
            ),
          })}
    </section>
  );
};

SelectedPost.propTypes = {
  data: PropTypes.shape({
    content_type: PropTypes.oneOf(["single_post", "flexible_content"]),
    padding_component: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),

    // single post
    selected_post: PropTypes.shape({
      post_url: PropTypes.string,
      featured_image: PropTypes.string,
      post_title: PropTypes.string,
      categories: PropTypes.arrayOf(PropTypes.string),
    }),

    // flexible content
    content: PropTypes.shape({
      background_component: PropTypes.shape({
        background_3d_model_file: PropTypes.any,
        background_3d_model: PropTypes.any,
        background_color: PropTypes.any,
        background_embed: PropTypes.any,
        background_image_position: PropTypes.any,
        background_image: PropTypes.any,
        background_type: PropTypes.any,
        background_video: PropTypes.any,
      }),
      eyebrow_component: PropTypes.oneOfType([PropTypes.bool, PropTypes.any]),
      heading_text: PropTypes.string,
      show_button_tags: PropTypes.oneOf(["tags", "button"]),
      tags: PropTypes.arrayOf(
        PropTypes.shape({
          tag_text: PropTypes.string,
        })
      ),
      tags_position: PropTypes.oneOf(["flex-start", "center", "flex-end"]),
      button_component: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        target: PropTypes.string,
      }),
    }),
  }),
};

export default SelectedPost;
